import React, { useState, useEffect, useMemo, useRef } from 'react';
import { buttonClass } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import Alert from '../components/ui/Alert';
import DropZone from '../components/ui/DropZone';
import Tag from '../components/ui/Tag';
import Reveal from '../components/ui/Reveal';
import Checkbox from '../components/ui/Checkbox';
import { Field, TextArea } from '../components/ui/Field';
import { useToast } from '../components/Toast/ToastContext';
import { useFirmwareApi, shortSha } from './firmwareApi';
import { clientSha256, autoDetectMeta, buildFileName, firmwareExt, TAG_BUILD_BRANCH } from './firmwareParse';
import { CATEGORY_LABELS } from './types';
import type { Firmware, FirmwareCategory, FirmwareModel, ArchPreset } from './types';

// upload    — publish a build (may resolve to an existing one from the file's hash)
// attach    — put the FIRST file on an existing file-less telemetry label
// addTarget — put ANOTHER file on a build that already has one (multi-target)
// edit      — build metadata only
type Mode = 'upload' | 'attach' | 'addTarget' | 'edit';

interface Props {
  mode: Mode;
  category: FirmwareCategory;
  models: FirmwareModel[];
  archPresets: ArchPreset[];
  existing?: Firmware | null; // attach/addTarget: the target build; edit: the build
  attachable?: Firmware[]; // file-less px4 telemetry labels (candidates for prefix-match attach)
  published?: Firmware[]; // already-published builds in this category (candidates for add-target)
  onClose: () => void;
  onSaved: () => void;
}

// Per-category accept lists — mirror the server's ALLOWED_EXT_BY_CATEGORY:
// px4/wifi/lora take flashable images, drone_vision takes the signed bundle,
// scanner_shell takes the scanner app.
const IMAGE_ACCEPT = '.bin,.px4,.hex,.elf';
const BUNDLE_ACCEPT = '.tar.gz,.tgz';
const APK_ACCEPT = '.apk';
const ACCEPT_SIG = '.sig';
const MAX_BYTES = 50 * 1024 * 1024; // mirror the server cap — reject before hashing
const MAX_SIG_BYTES = 1024 * 1024; // mirror SIG_MAX_BYTES

const todayLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Prefix match in either direction: telemetry labels store a 16-char hash while a
// file yields the full 40-char.
const hashMatches = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const x = (a || '').toLowerCase();
  const y = (b || '').toLowerCase();
  if (!x || !y) return false;
  return x.startsWith(y) || y.startsWith(x);
};

export default function UploadFirmwareModal({
  mode,
  category,
  models,
  archPresets,
  existing,
  attachable = [],
  published = [],
  onClose,
  onSaved,
}: Props) {
  const toast = useToast();
  const fw = useFirmwareApi();

  const [file, setFile] = useState<File | null>(null);
  const [sigFile, setSigFile] = useState<File | null>(null);
  const [sha, setSha] = useState('');
  const [computing, setComputing] = useState(false);
  const [label, setLabel] = useState(existing?.label || '');
  const [version, setVersion] = useState(existing?.version || '');
  const [gitHash, setGitHash] = useState(existing?.git_hash || '');
  const [branch, setBranch] = useState(existing?.branch || '');
  const [architecture, setArchitecture] = useState(mode === 'edit' ? existing?.architecture || '' : '');
  const [releaseDate, setReleaseDate] = useState(existing?.release_date || todayLocal());
  const [notes, setNotes] = useState(existing?.notes || '');
  const [selModels, setSelModels] = useState<string[]>(mode === 'edit' ? existing?.applicable_models || [] : []);
  const [isActive, setIsActive] = useState(mode === 'edit' ? !!existing?.is_active : true);
  const [saving, setSaving] = useState(false);
  const [ackNoHash, setAckNoHash] = useState(false); // armed after a no-hash confirm
  const autoLabelRef = useRef(existing?.label || ''); // last value WE auto-filled into Label (to detect a hand-edit)
  // What the form opened with, for the discard question. Taken once; the dialog mounts per opening.
  const opened = useRef({
    label: existing?.label || '',
    version: existing?.version || '',
    gitHash: existing?.git_hash || '',
    branch: existing?.branch || '',
    architecture: mode === 'edit' ? existing?.architecture || '' : '',
    releaseDate: existing?.release_date || todayLocal(),
    notes: existing?.notes || '',
    models: [...(mode === 'edit' ? existing?.applicable_models || [] : [])].sort().join('|'),
    isActive: mode === 'edit' ? !!existing?.is_active : true,
  }).current;

  const needsFile = mode !== 'edit';
  // The scanner app names itself: the server reads its version from the APK and
  // labels it when Label is blank. It fits no drone model.
  const isShell = category === 'scanner_shell';
  const accept = isShell ? APK_ACCEPT : category === 'drone_vision' ? BUNDLE_ACCEPT : IMAGE_ACCEPT;
  const trimmedHash = gitHash.trim();

  // Which already-published build (if any) this upload will land on. In upload
  // mode we discover it from the file's / typed git hash; in addTarget mode the
  // caller bound it explicitly.
  const publishedMatch = useMemo(() => {
    if (mode === 'addTarget') return existing || null;
    if (mode !== 'upload' || trimmedHash.length < 7) return null;
    return published.find((b) => hashMatches(b.git_hash, trimmedHash)) || null;
  }, [mode, existing, published, trimmedHash]);

  // Adding a target: the build's identity is already fixed by its first file, so
  // the build fields are shown read-only and the server ignores them. That is the
  // whole reason there is no "which label wins" ambiguity across targets.
  const isAddTarget = !!publishedMatch;
  const buildLocked = isAddTarget;
  const hashLocked = mode === 'attach' || isAddTarget; // bound to a specific record's hash

  // Keep the form mirroring the bound build (covers upload mode discovering it late).
  useEffect(() => {
    if (!publishedMatch) return;
    setLabel(publishedMatch.label || '');
    setVersion(publishedMatch.version || '');
    setBranch(publishedMatch.branch || '');
    setReleaseDate(publishedMatch.release_date || todayLocal());
    setNotes(publishedMatch.notes || '');
    setGitHash(publishedMatch.git_hash || '');
    setIsActive(!!publishedMatch.is_active);
  }, [publishedMatch]);

  // The hash the dropped file actually carries — used to warn when it disagrees
  // with the build we're adding a target to (that file is a different commit).
  const [fileGitHash, setFileGitHash] = useState('');

  const handleFile = async (f: File | null) => {
    if (f && f.size > MAX_BYTES) { toast.error('File exceeds the 50 MB limit'); return; }
    setFile(f);
    setSha('');
    setFileGitHash('');
    setAckNoHash(false);
    if (!f) return;
    setComputing(true);
    // Hash + auto-detect in parallel. A .px4 envelope yields git hash + version +
    // architecture; a raw .bin/.elf yields git hash + version + branch (from the
    // embedded version strings) — so the engineer mostly just confirms.
    try {
      const [digest, meta] = await Promise.all([
        clientSha256(f),
        autoDetectMeta(f, archPresets),
      ]);
      setSha(digest);
      setFileGitHash(meta.gitHash || '');
      // Architecture always re-parses: it is per-FILE, so each target's file
      // supplies its own.
      setArchitecture(meta.architecture || '');
      // A (re)selected file re-parses the file-derived fields: overwrite them with
      // THIS file's values (clearing when absent) so nothing goes stale from a prior
      // pick. Skipped when the fields belong to a bound build (locked).
      if (!hashLocked) setGitHash(meta.gitHash || '');
      if (!buildLocked) {
        setVersion(meta.version || '');
        setBranch(meta.branch || '');
        // Only a bundle's manifest carries a build date. Re-derive it per file
        // like the fields above (falling back to today when THIS file has none),
        // or a replaced bundle would silently keep the previous one's date.
        // Images never carry a date, so px4/wifi/lora keep the field as-is.
        if (category === 'drone_vision') setReleaseDate(meta.releaseDate || todayLocal());
      }
      // Label auto-fill: an existing telemetry label wins (single source — editing
      // it here rewrites that record on attach); else a release-tag build's version
      // becomes its label. Left alone for a plain dev-branch file (user types it).
      if (!buildLocked) {
        const gh = meta.gitHash || '';
        const match = gh ? attachable.find((r) => hashMatches(r.git_hash, gh)) : undefined;
        const autoLabel = (match && match.label)
          // A bundle names itself: manifest name + version ("drone_vision 0.0.11"), freely editable.
          || (meta.name && meta.version ? `${meta.name} ${meta.version}` : '')
          || (meta.branch === TAG_BUILD_BRANCH ? meta.version || '' : '');
        // Refresh from the new file, but never clobber a hand-edit: overwrite only
        // when the field is empty or still holds the value we last auto-filled.
        if (autoLabel && (!label.trim() || label === autoLabelRef.current)) {
          autoLabelRef.current = autoLabel;
          setLabel(autoLabel);
        }
      }
    } catch {
      setSha('');
    } finally {
      setComputing(false);
    }
  };

  const handleSigFile = (f: File | null) => {
    if (f && f.size > MAX_SIG_BYTES) { toast.error('Signature file exceeds the 1 MB limit'); return; }
    setSigFile(f);
  };

  const toggleModel = (key: string) =>
    setSelModels((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  // Telemetry labels carry a 16-char hash; the file gives 40 — match by prefix
  // (either direction) so the auto-detected hash surfaces the attach.
  const attachMatch = useMemo(() => {
    if (mode !== 'upload' || isAddTarget || trimmedHash.length < 7) return null;
    return attachable.find((r) => hashMatches(r.git_hash, trimmedHash)) || null;
  }, [trimmedHash, attachable, mode, isAddTarget]);
  const attachNotice = attachMatch ? attachMatch.label : null;

  // Typed-hash flow: if the entered hash matches a telemetry label and the label
  // field is still empty, fill it from that label (the file-drop flow fills it in
  // handleFile). Only when empty, so it never clobbers what the user typed.
  useEffect(() => {
    if (mode === 'upload' && attachMatch && attachMatch.label && !label.trim()) {
      autoLabelRef.current = attachMatch.label;
      setLabel(attachMatch.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachMatch]);
  const warnNoHash = needsFile && !isShell && !trimmedHash && ackNoHash;

  // Dropping a file from a DIFFERENT commit into an add-target flow would publish
  // it under this build's hash. Allowed (the engineer may know better) but called out.
  const hashMismatch = isAddTarget && !!fileGitHash && !!trimmedHash && !hashMatches(fileGitHash, trimmedHash);

  // Same architecture as a target this build already carries -> the derived
  // filename will collide and the server will 409. Warn before the round trip.
  const archCollision = useMemo(() => {
    if (!publishedMatch || !architecture.trim()) return null;
    const a = architecture.trim().toLowerCase();
    return publishedMatch.files.find((f) => (f.architecture || '').trim().toLowerCase() === a) || null;
  }, [publishedMatch, architecture]);

  const previewName =
    needsFile && file
      ? buildFileName({
          category,
          architecture,
          releaseDate,
          branch,
          label,
          gitHash,
          sha256: sha,
          ext: firmwareExt(file.name),
          presets: archPresets,
        })
      : '';

  const canSubmit = (isShell || (!!label.trim() && selModels.length > 0)) && (!needsFile || !!file) && !saving && !computing;

  const titles: Record<Mode, string> = {
    upload: 'Upload Firmware',
    attach: 'Attach Firmware',
    addTarget: 'Add Target File',
    edit: 'Edit Firmware',
  };
  const title = isAddTarget && mode === 'upload' ? titles.addTarget : titles[mode];

  const handleSubmit = async () => {
    if (!canSubmit) return;
    // First attempt to publish a file with no git hash: warn instead of submit.
    // The git hash links telemetry + is the recognizable id; sha256 is the fallback.
    if (needsFile && !isShell && !trimmedHash && !ackNoHash) { setAckNoHash(true); return; }
    setSaving(true);
    try {
      if (mode === 'edit' && existing) {
        await fw.update(existing.id, {
          label: label.trim(),
          version: version.trim() || null,
          git_hash: gitHash.trim() || null,
          branch: branch.trim() || null,
          release_date: releaseDate || null,
          notes: notes.trim() || null,
          is_active: isActive,
          // architecture + applicable_models are per-FILE. Only forward them for a
          // single-target build (the server 400s on a multi-target build and the
          // per-target editor on the build page is the right surface there).
          ...(existing.files.length === 1 && !isShell
            ? { architecture: architecture.trim() || null, applicable_models: selModels }
            : {}),
        });
        toast.success('Firmware updated');
      } else if (file) {
        const res = await fw.publish({
          category,
          label: label.trim(),
          version: version.trim() || undefined,
          git_hash: gitHash.trim() || undefined,
          branch: branch.trim() || undefined,
          architecture: architecture.trim() || undefined,
          // Never blank: the server would fall back to the manifest's date while
          // the preview falls back to today. Sending the fallback explicitly
          // keeps the name on screen the name that gets stored.
          release_date: releaseDate || todayLocal(),
          notes: notes.trim() || undefined,
          applicable_models: selModels,
          is_active: isActive,
          file,
          sig: sigFile,
        });
        toast.success(
          res.addedTarget ? 'Target file added to the build'
            : res.attached ? 'Attached to the existing record'
              : 'Firmware published',
        );
      }
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // A bound build's locked fields mirror the record (its active flag can differ from the opening
  // default), so they never count. The models stay editable per file, and a typed hash is what bound it.
  const dirty = !!file || !!sigFile || architecture !== opened.architecture || gitHash !== opened.gitHash
    || [...selModels].sort().join('|') !== opened.models || (!buildLocked && (
      label !== opened.label || version !== opened.version || branch !== opened.branch
      || releaseDate !== opened.releaseDate || notes !== opened.notes || isActive !== opened.isActive
    ));

  const submitLabel = saving
    ? 'Saving…'
    : mode === 'edit'
      ? 'Save'
      : warnNoHash
        ? 'Publish anyway'
        : isAddTarget
          ? 'Add target'
          : 'Publish';

  const footer = (
    <>
      <button onClick={onClose} disabled={saving} className={buttonClass('quiet', 'md')}>Cancel</button>
      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className={buttonClass(warnNoHash ? 'warn' : 'filled', 'md')}
      >
        {submitLabel}
      </button>
    </>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      dirty={dirty}
      title={title}
      meta={<span className="text-xs uppercase tracking-wide">{CATEGORY_LABELS[category]}</span>}
      footer={footer}
    >
      <div className="space-y-4">
          {/* Add-target banner: this file joins an existing build */}
          <Reveal show={!!(isAddTarget && publishedMatch)}>
            {publishedMatch && (
            <Alert tone="info" variant="note" className="!flex w-full">
              Adding a target file to <span className="font-semibold">{publishedMatch.label}</span>
              {' '}({publishedMatch.files.length} {publishedMatch.files.length === 1 ? 'target' : 'targets'} today).
              The build's label, version, branch and git hash belong to the commit, so they stay as they are.
            </Alert>
            )}
          </Reveal>

          {/* File dropzone (upload + attach + addTarget) */}
          {needsFile && (
            <DropZone
              what="a firmware file"
              hint={`Takes ${accept.split(',').join(', ')}`}
              accept={accept}
              chosen={file}
              onFiles={(files) => handleFile(files[0] || null)}
            />
          )}

          {/* Optional detached signature for THIS file */}
          {needsFile && (
            <DropZone
              size="sm"
              what="a signing file (.sig, optional)"
              hint="You can also attach one later from the build page."
              accept={ACCEPT_SIG}
              chosen={sigFile}
              onFiles={(files) => handleSigFile(files[0] || null)}
            />
          )}

          {/* SHA-256 — computed (display only) */}
          <Reveal show={!!(needsFile && file)}>
            <div className="text-xs text-on-surface-variant flex items-center gap-2 flex-wrap">
              <span>SHA-256</span>
              {computing ? (
                <span className="text-on-surface-variant">computing…</span>
              ) : (
                <Tag variant="code" mono>{shortSha(sha)}</Tag>
              )}
              <span className="text-on-surface-variant">· server verifies on upload</span>
            </div>
          </Reveal>
          {mode === 'edit' && existing?.sha256 && (
            <div className="text-xs text-on-surface-variant flex items-center gap-2">
              <span>SHA-256</span>
              <Tag variant="code" mono>{shortSha(existing.sha256)}</Tag>
              <span className="text-on-surface-variant font-mono break-all">· {existing.file_name}</span>
            </div>
          )}

          {/* Filename preview — shows the canonical name the server will assign */}
          <Reveal show={!!previewName}>
            <div className="text-xs text-on-surface-variant flex items-start gap-2 flex-wrap">
              <span className="pt-0.5">Filename</span>
              <code className="text-primary/90 bg-surface-container-highest px-2 py-0.5 rounded font-mono break-all flex-1 min-w-0">{previewName}</code>
            </div>
          </Reveal>

          {/* Label + version */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-on-surface-variant mb-1">Label{isShell ? '' : ' *'}</label>
              <Field value={label} maxLength={100} onChange={(e) => setLabel(e.target.value)} placeholder={isShell ? 'Fleet Scanner and its version name' : 'e.g. Spring 2026 Stable'} disabled={saving || buildLocked} />
            </div>
            <div>
              <label className="block text-xs text-on-surface-variant mb-1">Version</label>
              <Field value={isShell && mode !== 'edit' ? '' : version} maxLength={100} onChange={(e) => setVersion(e.target.value)} placeholder={isShell ? 'Read from the app' : 'e.g. v2.4.1 (optional)'} disabled={saving || buildLocked || isShell} />
            </div>
          </div>

          {/* Branch + release date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-on-surface-variant mb-1">Branch</label>
              <Field value={branch} maxLength={100} onChange={(e) => setBranch(e.target.value)} placeholder="e.g. Matt-KIWISTRIKE" disabled={saving || buildLocked} />
            </div>
            <div>
              <label className="block text-xs text-on-surface-variant mb-1">Release date</label>
              <Field type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)} disabled={saving || buildLocked} />
            </div>
          </div>

          {/* Architecture — the build TARGET. Per file: this is what distinguishes
              two files of the same commit. */}
          {!isShell && (
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">
              Architecture <span className="text-on-surface-variant">(the build target · auto-detected · editable)</span>
            </label>
            <Field
              className="font-mono"
              list="fw-arch-presets"
              value={architecture}
              maxLength={120}
              onChange={(e) => setArchitecture(e.target.value)}
              placeholder="e.g. ark_pi6x_default"
              disabled={saving}
            />
            <datalist id="fw-arch-presets">
              {archPresets.map((p) => (
                <option key={p.token} value={p.arch}>{p.token}</option>
              ))}
            </datalist>
            <Reveal show={!!archCollision}>
              {archCollision && (
              <Alert tone="warning" variant="note" className="mt-1 !flex w-full">
                This build already has a file for <span className="font-mono">{archCollision.architecture}</span>. Two files
                of one commit need different targets, or the filename collides. Change the architecture, or remove the
                existing target from the build page first.
              </Alert>
              )}
            </Reveal>
          </div>
          )}

          {/* Git hash (px4 link) */}
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">
              Git hash {category === 'px4' ? '(links to telemetry labels · auto-read from .px4)' : '(optional)'}
            </label>
            <Field
              className="font-mono"
              value={gitHash}
              onChange={(e) => setGitHash(e.target.value)}
              placeholder="optional"
              disabled={saving || hashLocked}
            />
            <Reveal show={!!attachNotice}>
              {attachNotice && (
              <Alert tone="success" variant="note" className="mt-1 !flex w-full">
                Hash already detected (label "{attachNotice}") — this will attach to the existing record.
              </Alert>
              )}
            </Reveal>
            {mode === 'attach' && (
              <p className="mt-1 text-xs text-on-surface-variant">Attaching to this telemetry version — the file will be added to its existing label.</p>
            )}
            {isAddTarget && (
              <p className="mt-1 text-xs text-on-surface-variant">Locked to the build's commit — every target file under a build shares one git hash.</p>
            )}
            <Reveal show={!!hashMismatch}>
              {hashMismatch && (
              <Alert tone="warning" variant="note" className="mt-1 !flex w-full">
                This file reports git hash <span className="font-mono">{fileGitHash.slice(0, 12)}</span>, which is not this
                build's commit. It will still be filed under this build. Publish it on its own if it is a different commit.
              </Alert>
              )}
            </Reveal>
            <Reveal show={!!warnNoHash}>
              {warnNoHash && (
              <Alert tone="warning" variant="note" className="mt-1 !flex w-full">
                No git hash — this build will be identified by its SHA-256 ({shortSha(sha)}). The git hash links
                telemetry and is easier to recognize. Add one above, or press <span className="font-semibold">Publish anyway</span>.
              </Alert>
              )}
            </Reveal>
          </div>

          {/* Applicable models — per FILE: which drones flash THIS target */}
          {!isShell && (
          <div>
            <label className="block text-xs text-on-surface-variant mb-1.5">
              Applies to * <span className="text-on-surface-variant">(which drones flash this file)</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {models.map((m) => (
                <Checkbox
                  key={m.key}
                  label={m.name}
                  checked={selModels.includes(m.key)}
                  onChange={() => toggleModel(m.key)}
                  disabled={saving}
                />
              ))}
            </div>
            {mode === 'edit' && existing && existing.files.length > 1 && (
              <p className="mt-1.5 text-xs text-on-surface-variant">
                This build has {existing.files.length} target files, each with its own architecture and models. Edit those
                on the build page; changes here apply to the build only.
              </p>
            )}
          </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs text-on-surface-variant mb-1">Release notes</label>
            <TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="optional" disabled={saving || buildLocked} />
          </div>

          {/* Active */}
          <Checkbox label="Mark as currently active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} disabled={saving || buildLocked} />
      </div>
    </Dialog>
  );
}
